/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { DocumentListComponent } from '@alfresco/adf-content-services';
import { NodeEntry } from '@alfresco/js-api';
import { TranslationService } from '@alfresco/adf-core';
import { Store } from '@ngrx/store';
import { SetInfoDrawerStateAction, SetSelectedNodesAction } from '@alfresco/aca-shared/store';
import { CommentSyncService } from './comment-sync.service';

@Injectable({ providedIn: 'root' })
export class CommentColumnService {
  private readonly store = inject<Store<any>>(Store);
  private readonly comments = inject(CommentSyncService);
  private readonly translation = inject(TranslationService);
  private readonly document = inject(DOCUMENT);

  getCount(row: any): number {
    const nodeId = row?.getId?.();
    if (!nodeId) {
      return 0;
    }
    const cached = this.comments.peekCommentCount(nodeId);
    const properties = this.getNode(row)?.entry?.properties ?? {};
    return cached || Number(properties['fm:commentCount'] ?? 0);
  }

  hydrate(rows: any[]): void {
    for (const row of rows ?? []) {
      const nodeId = row?.getId?.();
      if (nodeId) {
        const properties = this.getNode(row)?.entry?.properties ?? {};
        this.comments.updateCommentCount(nodeId, Number(properties['fm:commentCount'] ?? 0));
      }
    }
  }

  open(event: Event, row: any, documentList: DocumentListComponent): void {
    event.stopPropagation();
    const node = this.getNode(row);
    if (!node?.entry) {
      return;
    }
    documentList.resetSelection();
    documentList.preselectNodes = [node];
    documentList.onPreselectNodes();
    this.openForNode(node);
  }

  openForNode(node: NodeEntry): void {
    if (!node?.entry) {
      return;
    }
    this.store.dispatch(new SetSelectedNodesAction([node]));
    this.store.dispatch(new SetInfoDrawerStateAction(true));
    this.selectCommentsTab();
  }

  openForNodeAfterContextMenu(node: NodeEntry, attempt = 0): void {
    const menuIsClosing = !!this.document.querySelector('.cdk-overlay-pane .mat-mdc-menu-panel');
    if (menuIsClosing && attempt < 40) {
      window.setTimeout(() => this.openForNodeAfterContextMenu(node, attempt + 1), 50);
      return;
    }
    window.setTimeout(() => this.openForNode(node), 50);
  }

  private getNode(row: any): NodeEntry | null {
    return (row?.obj ?? row?.node ?? null) as NodeEntry | null;
  }

  private selectCommentsTab(attempt = 0): void {
    const expectedLabel = this.translation.instant('APP.INFO_DRAWER.TABS.COMMENTS').trim();
    const tabs = Array.from(this.document.querySelectorAll<HTMLElement>('.aca-sidebar [role="tab"]'));
    const commentsTab = tabs.find((tab) => tab.textContent?.trim() === expectedLabel);
    if (commentsTab) {
      commentsTab.click();
    } else if (attempt < 40) {
      window.setTimeout(() => this.selectCommentsTab(attempt + 1), 50);
    }
  }
}
